import React, { memo, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import useTopPanel from 'hooks/useTopPanel';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useNotification from 'hooks/useNotification';

const RejectReasons = () => {
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();

  // Rad etish sabablari
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null); // {id, name} | null

  // Sifatsizlik sabablari
  const [newTrashName, setNewTrashName] = useState('');
  const [savingTrash, setSavingTrash] = useState(false);
  const [editTrash, setEditTrash] = useState(null); // {id, name} | null

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('common.fields.rejectReason')}</div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const { isLoading, isFetching, data, refetch } = useQuery({
    queryKey: ['/dictionary/rejectreasons'],
    queryFn: async () => {
      const res = await axiosPrivate.get('/dictionary/rejectreasons');
      return res.data.data;
    },
    retry: false,
  });

  const {
    isLoading: trashLoading,
    isFetching: trashFetching,
    data: trashData,
    refetch: refetchTrash,
  } = useQuery({
    queryKey: ['/dictionary/trashreasons'],
    queryFn: async () => {
      const res = await axiosPrivate.get('/dictionary/trashreasons');
      return res.data.data;
    },
    retry: false,
  });

  const add = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    try {
      await axiosPrivate.post('/dictionary/rejectreasons', { name });
      setNewName('');
      refetch();
      sendNotification({ msg: 'Qo\'shildi', variant: 'success' });
    } catch (e) {
      sendNotification({ msg: 'Xatolik', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const update = async () => {
    const name = (editItem?.name || '').trim();
    if (!name || !editItem?.id || saving) return;
    setSaving(true);
    try {
      await axiosPrivate.post(`/dictionary/rejectreasons/${editItem.id}`, { name });
      setEditItem(null);
      refetch();
      sendNotification({ msg: 'Yangilandi', variant: 'success' });
    } catch (e) {
      sendNotification({ msg: 'Xatolik', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const addTrash = async () => {
    const name = newTrashName.trim();
    if (!name || savingTrash) return;
    setSavingTrash(true);
    try {
      await axiosPrivate.post('/dictionary/trashreasons', { name });
      setNewTrashName('');
      refetchTrash();
      sendNotification({ msg: 'Qo\'shildi', variant: 'success' });
    } catch (e) {
      sendNotification({ msg: 'Xatolik', variant: 'error' });
    } finally {
      setSavingTrash(false);
    }
  };

  const updateTrash = async () => {
    const name = (editTrash?.name || '').trim();
    if (!name || !editTrash?.id || savingTrash) return;
    setSavingTrash(true);
    try {
      await axiosPrivate.post(`/dictionary/trashreasons/${editTrash.id}`, { name });
      setEditTrash(null);
      refetchTrash();
      sendNotification({ msg: 'Yangilandi', variant: 'success' });
    } catch (e) {
      sendNotification({ msg: 'Xatolik', variant: 'error' });
    } finally {
      setSavingTrash(false);
    }
  };

  const list = Array.isArray(data) ? data : [];
  const trashList = Array.isArray(trashData) ? trashData : [];

  return (
    <div className='component-list-wrapper'>
      <Grid container spacing={2} className='!mt-2' alignItems='flex-start'>
        {/* Rad etish sabablari */}
        <Grid item xs={12} md={6}>
        <Card className='!p-4 w-full'>
          <div className='font-semibold mb-3'>
            {t('common.fields.rejectReason')}
          </div>
          <div className='flex items-center gap-2 mb-4'>
            <TextField
              size='small'
              fullWidth
              label={t('common.fields.rejectReason')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <Button
              variant='contained'
              color='success'
              onClick={add}
              disabled={saving || !newName.trim()}
            >
              {saving ? (
                <CircularProgress size={18} color='inherit' />
              ) : (
                <i className='bi bi-plus-circle' />
              )}
            </Button>
          </div>

          {isLoading || isFetching ? (
            <div className='flex items-center justify-center py-6'>
              <CircularProgress size={26} />
            </div>
          ) : list.length === 0 ? (
            <div className='text-center text-gray-400 py-6'>—</div>
          ) : (
            <div className='flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1'>
              {list.map((item) => (
                <div
                  key={item.id}
                  className='flex items-center justify-between border rounded-lg px-3 py-2'
                >
                  <span>{item.name}</span>
                  <IconButton
                    size='small'
                    color='warning'
                    onClick={() => setEditItem({ id: item.id, name: item.name })}
                  >
                    <i className='bi bi-pencil-square text-base' />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </Card>
        </Grid>

        {/* Sifatsizlik sabablari */}
        <Grid item xs={12} md={6}>
        <Card className='!p-4 w-full'>
          <div className='font-semibold mb-3'>Sifatsizlik sababi</div>
          <div className='flex items-center gap-2 mb-4'>
            <TextField
              size='small'
              fullWidth
              label='Sifatsizlik sababi'
              value={newTrashName}
              onChange={(e) => setNewTrashName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTrash();
                }
              }}
            />
            <Button
              variant='contained'
              color='success'
              onClick={addTrash}
              disabled={savingTrash || !newTrashName.trim()}
            >
              {savingTrash ? (
                <CircularProgress size={18} color='inherit' />
              ) : (
                <i className='bi bi-plus-circle' />
              )}
            </Button>
          </div>

          {trashLoading || trashFetching ? (
            <div className='flex items-center justify-center py-6'>
              <CircularProgress size={26} />
            </div>
          ) : trashList.length === 0 ? (
            <div className='text-center text-gray-400 py-6'>—</div>
          ) : (
            <div className='flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1'>
              {trashList.map((item) => (
                <div
                  key={item.id}
                  className='flex items-center justify-between border rounded-lg px-3 py-2'
                >
                  <span>{item.name}</span>
                  <IconButton
                    size='small'
                    color='warning'
                    onClick={() => setEditTrash({ id: item.id, name: item.name })}
                  >
                    <i className='bi bi-pencil-square text-base' />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </Card>
        </Grid>
      </Grid>

      {/* Rad etish tahrirlash oynasi */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth='xs' fullWidth>
        <DialogTitle>{t('common.fields.rejectReason')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            fullWidth
            value={editItem?.name || ''}
            onChange={(e) =>
              setEditItem((s) => ({ ...s, name: e.target.value }))
            }
            onKeyDown={(e) => e.key === 'Enter' && update()}
          />
        </DialogContent>
        <DialogActions>
          <Button color='error' onClick={() => setEditItem(null)}>
            {t('common.button.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={update}
            disabled={saving || !(editItem?.name || '').trim()}
          >
            {t('common.button.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sifatsizlik tahrirlash oynasi */}
      <Dialog open={!!editTrash} onClose={() => setEditTrash(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Sifatsizlik sababi</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            fullWidth
            value={editTrash?.name || ''}
            onChange={(e) =>
              setEditTrash((s) => ({ ...s, name: e.target.value }))
            }
            onKeyDown={(e) => e.key === 'Enter' && updateTrash()}
          />
        </DialogContent>
        <DialogActions>
          <Button color='error' onClick={() => setEditTrash(null)}>
            {t('common.button.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={updateTrash}
            disabled={savingTrash || !(editTrash?.name || '').trim()}
          >
            {t('common.button.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default memo(RejectReasons);
